import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { Trans, useTranslation } from 'react-i18next'

import { challengeTypes, mfaTypes } from 'components/Cognito/types'
import { useCognito, useCognitoDispatch } from 'components/Cognito/CognitoContextProvider'

import { TickWrappedIcon } from 'images'

function ResendButton({ onResend }) {
  const { t } = useTranslation()
  const { resendMFACodeViaEmail } = useCognitoDispatch()
  const { loading } = useCognito()
  const [sent, setSent] = useState(false)

  const resendCallback = useCallback(
    ({ success, hasChallenge }) => {
      if (!success && hasChallenge) {
        if (onResend) {
          onResend()
        }
        setSent(true)
        setTimeout(() => setSent(false), 2000)
      }
    },
    [setSent, onResend],
  )

  return (
    <div className="mfa-challenge__switcher__resend-wrapper">
      <div className="mfa-challenge__switcher__resend-wrapper__btn-wrapper">
        {!sent && (
          <Trans
            i18nKey="Login.mfa.resendEmailCode"
            components={{
              button: (
                <button
                  disabled={loading || sent}
                  onClick={() => {
                    setSent(false)
                    resendMFACodeViaEmail(resendCallback)
                  }}
                  className="mfa-challenge__switcher__btn icon-button"
                >
                  Resend
                </button>
              ),
            }}
          />
        )}
        {sent && (
          <div className="mfa-challenge__switcher__resend-wrapper__sent">
            <TickWrappedIcon className="resent-icon" width={14} height={14} />
            <span>{t('common.sent')}</span>
          </div>
        )}
      </div>
    </div>
  )
}

ResendButton.propTypes = {
  onResend: PropTypes.func,
}

export default function MFASwitcher({ onResend, onSwitch }) {
  const {
    loading,
    user: { mfaSettings },
    challenge: { name: challengeName },
  } = useCognito()
  const { switchMFAMethod } = useCognitoDispatch()
  if (!mfaSettings) {
    return null
  }
  const { preferred_mfa, activate_mfa } = mfaSettings

  // During MFA setup, user can also switch between email and authenticator app
  const hasAlternatives =
    challengeName === challengeTypes.MFA_SETUP ||
    !!activate_mfa?.filter((x) => x !== preferred_mfa)?.length

  return (
    <div className="mfa-challenge__switcher">
      {hasAlternatives && preferred_mfa === mfaTypes.softwareTokenMfa && (
        <Trans
          i18nKey="Login.mfa.switchMfaType.switchToEmail"
          components={{
            span: <span>Problem with authenticator app? Log-in via </span>,
            button: (
              <button
                onClick={() => {
                  switchMFAMethod(mfaTypes.emailTotp, () => {
                    if (onSwitch) {
                      onSwitch()
                    }
                  })
                }}
                className="mfa-challenge__switcher__btn"
                disabled={loading}
              >
                email
              </button>
            ),
          }}
        />
      )}
      {preferred_mfa === mfaTypes.emailTotp && (
        <>
          <div>
            <ResendButton onResend={onResend} />
          </div>
          {hasAlternatives && (
            <div className="mt-2">
              <Trans
                i18nKey="Login.mfa.switchMfaType.switchToAuthenticator"
                components={{
                  button: (
                    <button
                      onClick={() => {
                        switchMFAMethod(mfaTypes.softwareTokenMfa, () => {
                          if (onSwitch) {
                            onSwitch()
                          }
                        })
                      }}
                      className="mfa-challenge__switcher__btn"
                      disabled={loading}
                    >
                      authenticator app
                    </button>
                  ),
                }}
              />
            </div>
          )}
        </>
      )}
    </div>
  )
}

MFASwitcher.propTypes = {
  onResend: PropTypes.func,
  onSwitch: PropTypes.func,
}
